import "../styles/UserProfile.css";
import { React, useContext, useRef, useEffect, useState } from "react";
import axios from "axios";
import { ProfileAutocomplete, ValidateTextField } from "../components";
import { UserContext } from "../App";
import {
  MenuItem,
  FormControl,
  Select,
  Box,
  FormGroup,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  FormHelperText,
} from "@mui/material";
import { Close, CameraAlt as Camera, LinkedIn as LinkedInIcon, GitHub as GitHubIcon, InsertLink as InsertLinkIcon } from "@mui/icons-material";

function UserProfile() {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { username, profilePicture, updatedProfilePicture, setUpdatedProfilePicture } = useContext(UserContext);

  const [availableSkills, setAvailableSkills] = useState([]);
  const [availableResearchAreas, setAvailableResearchAreas] = useState([]);
  const [availableCompensations, setAvailableCompensations] = useState([]);
  const [availableJobLevels, setAvailableJobLevels] = useState([]);
  const [availableJobTypes, setAvailableJobTypes] = useState([]);
  const [availableJobIndustries, setAvailableJobIndustries] = useState([]);
  const availableYears = ["Freshman", "Sophomore", "Junior", "Senior", "Masters", "PhD"];
  const availableMajorMinor = ["Major", "Minor"];

  // Inputted values
  const [selectedJobLevels, setSelectedJobLevels] = useState([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [selectedJobIndustries, setSelectedJobIndustries] = useState([]);
  const [selectedResearchAreas, setSelectedResearchAreas] = useState([]);
  const [selectedCompensations, setSelectedCompensations] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMajor, setSelectedMajor] = useState("");
  const [isMajorError, setIsMajorError] = useState(!selectedMajor);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [github, setGithub] = useState("");
  const [website, setWebsite] = useState("");
  const [graduationYear, setGraduationYear] = useState("");
  const [bio, setBio] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [reloadProfile, setReloadProfile] = useState(false);
  const [actualSelectedFile, setActualSelectedFile] = useState(null);
  const [jobEmailOptIn, setJobEmailOptIn] = useState(false);
  const [resPosEmailOptIn, setResPosEmailOptIn] = useState(false);
  const [resume, setResume] = useState(null);
  const [resumeName, setResumeName] = useState("");

  const [isLoading, setIsLoading] = useState(true); // Initially set to true

  useEffect(() => {
    setIsLoading(true); // Start loading
    const body = new FormData();
    body.append("pid", username);

    axios.post("/student/viewProfile", body).then((res) => {
      const data = res.data;
      if (data.success) {
        setProfileValues(data.student);
      }
    }).finally(() => {
      setIsLoading(false); // Data has been loaded, or the request failed
    });
  }, [username, reloadProfile]);

  useEffect(() => {
    axios.get("/company/skills").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableSkills(data.skills);
      } else {
        return setAvailableSkills([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/company/jobLevels").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableJobLevels(data.jobLevels);
      } else {
        return setAvailableJobLevels([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/company/jobIndustries").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableJobIndustries(data.jobIndustries);
      } else {
        return setAvailableJobIndustries([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/compensations").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableCompensations(data.compensations);
      } else {
        return setAvailableCompensations([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/jobTypes").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableJobTypes(data.jobTypes);
      } else {
        return setAvailableJobTypes([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/researchAreas").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableResearchAreas(data.researchAreas);
      } else {
        return setAvailableResearchAreas([]);
      }
    });
  }, []);



  useEffect(() => {
    setSelectedFile(profilePicture);
  }, [profilePicture]);


  const setProfileValues = (student) => {
    setSelectedMajor(student.csType);
    setSelectedYear(student.academicYear);
    setFirstName(student.firstName);
    setLastName(student.lastName);
    setEmail(student.email);
    // Add dashes to 10-digit phone number
    if (student.phoneNumber.length === 10) {
      setPhone(student.phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3"));
    } else {
      setPhone(student.phoneNumber);
    }
    setGraduationYear(student.graduationYear);
    setBio(student.bio);
    setLinkedin(student.linkedInURL);
    setWebsite(student.websiteURL);
    setGithub(student.gitHubURL);
    setJobEmailOptIn(student.jobEmailNotif);
    setResPosEmailOptIn(student.resPosEmailNotif);
    setResumeName(student.resumeFileName);
    setResume(null);
    aRef.current.value = null;

    const jobTypes = [];
    const jobLevels = [];
    const skills = [];
    const industries = [];
    const researchAreas = [];
    const compensations = [];
    for (const tag in student.tags) {
      const tagName = student.tags[tag].name;
      const tagCategory = student.tags[tag].category;
      if (tagCategory !== "") {
        if (tagCategory === "jobInterest") {
          jobLevels.push(tagName);
        }
        if (tagCategory === "jobType") {
          jobTypes.push(tagName);
        }
        if (tagCategory === "skills") {
          skills.push(tagName);
        }
        if (tagCategory === "jobIndustry") {
          industries.push(tagName);
        }
        if (tagCategory === "researchArea") {
          researchAreas.push(tagName);
        }
        if (tagCategory === "compensation") {
          compensations.push(tagName);
        }
      }
    }
    setSelectedJobLevels(jobLevels);
    setSelectedJobTypes(jobTypes);
    setSelectedSkills(skills);
    setSelectedJobIndustries(industries);
    setSelectedResearchAreas(researchAreas);
    setSelectedCompensations(compensations);
    setUpdatedProfilePicture(!updatedProfilePicture);
  };

  const saveProfile = () => {
    const hasExistingResume = resume || (resumeName && resumeName !== "default.pdf");
    //Check for empty required fields
    let isValid = true;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !graduationYear.trim() || !hasExistingResume || !selectedMajor.trim()) {
      isValid = false;
    }
    // Stop the function if validation fails
    if (!isValid) {
      const missingFieldMessage = !hasExistingResume ? "Uploading a resume is required." : "You are missing one or more required fields.";
      setSnackbarMessage(missingFieldMessage);
      setSnackbarOpen(true);
      return;
    }

    const body = new FormData();
    body.append("pid", username);
    body.append("firstName", firstName);
    body.append("lastName", lastName);
    body.append("bio", bio);
    body.append("email", email);
    body.append("phoneNumber", phone);
    body.append("academicYear", selectedYear);
    body.append("graduationYear", graduationYear);
    body.append("linkedInURL", linkedin);
    body.append("gitHubURL", github);
    body.append("websiteURL", website);
    body.append("majorMinor", selectedMajor);
    body.append("jobInterests", selectedJobLevels);
    body.append("jobType", selectedJobTypes);
    body.append("industry", selectedJobIndustries);
    body.append("compensation", selectedCompensations);
    body.append("researchAreas", selectedResearchAreas);
    body.append("skills", selectedSkills);
    body.append("file", actualSelectedFile);
    body.append("jobEmailNotif", jobEmailOptIn);
    body.append("resPosEmailNotif", resPosEmailOptIn);
    body.append("resume", resume);

    axios.post("/student/updateProfile", body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((res) => {
        const data = res.data;
        if (data.success) {
          setSnackbarMessage("User profile updated!");
          setSnackbarOpen(true);
          setReloadProfile(!reloadProfile);
        } else {
          setSnackbarMessage(data.message);
          setSnackbarOpen(true);
        }
      });
  };

  let aRef = useRef(null);

  const handleFileChange = (event) => {
    const fileObj = event.target.files && event.target.files[0];
    // Make sure file exists
    if (!fileObj) {
      aRef.current.value = null;
      return;
    }

    // Make sure file is a pdf
    if (fileObj.type !== "application/pdf") {
      aRef.current.value = null;
      setSnackbarMessage("Please upload a pdf file");
      setSnackbarOpen(true);
      return;
    }

    // Make sure file is less than 2MB
    if (fileObj.size > 2 * 1024 ** 2) {
      aRef.current.value = null;
      setSnackbarMessage("Pdf file must be less than 2MB");
      setSnackbarOpen(true);
      return;
    }

    aRef = fileObj.name;
    setResume(fileObj);
  };

  const resetInput = () => {
    aRef.current.value = null;
    setResume(null);
  };

  const handleMajorChange = (event) => {
    const value = event.target.value;
    setSelectedMajor(value);
    setIsMajorError(value === "");
  };

  useEffect(() => {
    setIsMajorError(!selectedMajor);
  }, [selectedMajor]);

  function resetImage() {
    setSelectedFile(profilePicture);
    setActualSelectedFile(null);
  }

  const handleFileInput = (event) => {
    // Make sure file is less than 2MB
    if (event.target.files[0].size > 2 * 1024 ** 2) {
      setSnackbarMessage("Profile Picture must be less than 2MB");
      setSnackbarOpen(true);
      return;
    }
    setSelectedFile(URL.createObjectURL(event.target.files[0]));
    setActualSelectedFile(event.target.files[0]);
  };

  return (
    <div className="profile-body flex flex-col">
      <div className="outer-container">
        <div className="left-panel">
          <div className="picture-container">
            <form className="uploadBg" id="uploadBg">
              <a className="resetImage" onClick={resetImage}>
                <Close className="xmark" fontSize="small" />
              </a>
              <div className="imageUpload" id="imageUpload">
                <input
                  type="file"
                  onChange={handleFileInput}
                  title="Update Photo"
                ></input>
                {selectedFile && (
                  <img
                    className="pfp"
                    src={selectedFile}
                    alt="Selected Image"
                  />
                )}
                <div className="updatePhoto-container">
                  <Camera className="camera" fontSize="small" />
                </div>
              </div>
            </form>
          </div>

          <div className="bio-container">
            <p className="bio-title">Bio:</p>
            <textarea
              value={bio}
              onChange={(event) => {
                if (event.target.value.length <= 500) {
                  setBio(event.target.value);
                }
              }}
              id="bio"
              name="bio"
              type="textarea"
            />
          </div>

          <div className="url-container">
            <div className="linkedIn-container">
              {/* LinkedIn URL: */}
              <LinkedInIcon className="linkedIn-icon" fontSize="large" />
              <Box
                sx={{
                  width: 250,
                }}
              >
                <ValidateTextField
                  value={linkedin}
                  setter={setLinkedin}
                  pattern="(https?:\/\/)?(www\.)?linkedin\.com\/(?:in|pub)\/[a-zA-Z0-9_-]+\/?"
                  errorMessage={"Invalid LinkedIn url"}
                  required={false}
                  maxLength="500"
                />
              </Box>
            </div>

            <div className="git-container">
              {/* GitHub URL: */}
              <GitHubIcon className="github-icon" fontSize="large" />
              <Box
                sx={{
                  width: 250,
                }}
              >
                <ValidateTextField
                  value={github}
                  setter={setGithub}
                  pattern="(https:\/\/)?(www\.)?git(hub|lab)\.com\/[a-zA-Z0-9_-]+\/?"
                  errorMessage={"Invalid Github/Gitlab url"}
                  required={false}
                  maxLength="500"
                />
              </Box>
            </div>

            <div className="website-container">
              {/* Website: */}
              <InsertLinkIcon className="website-icon" fontSize="large" />
              <Box
                sx={{
                  width: 250,
                }}
              >
                <ValidateTextField
                  value={website}
                  setter={setWebsite}
                  pattern="(https:\/\/)?(www\.)?((?=[a-zA-Z])[a-zA-Z0-9_-]+(?<=[a-zA-Z])\.)+[a-zA-Z]{2,}\/?(\/?[a-zA-Z0-9-#?_]*)*"
                  errorMessage={"Invalid website url"}
                  required={false}
                  maxLength="500"
                />
              </Box>
            </div>
          </div>

          <div className="resume-container">
            Upload Resume as .pdf * ({resumeName && resumeName !== "default.pdf" ? `${resumeName} uploaded` : resume ? "Pending upload" : "None currently uploaded"}):
            <form
              className="upload"
              action="/student/{{name}}/uploadResume"
              method="POST"
              encType="multipart/form-data"
            >
              <label htmlFor="file-upload" className="custom-file-upload">
                <input
                  type="file"
                  ref={aRef}
                  onChange={handleFileChange}
                  className="file-input border"
                  placeholder="Resume"
                  name="resume"
                />
              </label>
              {!(resume || (resumeName && resumeName !== "default.pdf")) && <div className="error-message">Missing this required field.</div>}
            </form>
          </div>

        </div>

        <div className="right-panel">
          <div className="profileHeader-container">
            <p className="editProfile-text">Edit Profile:</p>
          </div>

          <div className="profile-info">
            {/* Left-side of user profile*/}
            <div className="profile-info-left">
              <div className="firstName-container">
                First Name: *
                <ValidateTextField
                  value={firstName}
                  setter={setFirstName}
                  pattern="[a-zA-Z-]+( [a-zA-Z-]+)*"
                  errorMessage={firstName ? "Invalid first name" : "Missing this required field"}
                  required={true}
                  maxLength="100"
                  isLoading={isLoading}
                />
              </div>
              <div className="lastName-container">
                Last Name: *
                <ValidateTextField
                  value={lastName}
                  setter={setLastName}
                  pattern="[a-zA-Z-]+( [a-zA-Z-]+)*"
                  errorMessage={lastName ? "Invalid last name" : "Missing this required field"}
                  required={true}
                  maxLength="100"
                  isLoading={isLoading}
                />
              </div>
              <div className="email-container">
                Email: *
                <ValidateTextField
                  value={email}
                  setter={setEmail}
                  pattern="[a-zA-Z0-9_.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}"
                  errorMessage={email ? "Invalid email" : "Missing this required field"}
                  required={true}
                  maxLength="100"
                  isLoading={isLoading}
                />
              </div>
              <div className="phone-container">
                Phone Number:
                <ValidateTextField
                  value={phone}
                  setter={setPhone}
                  pattern="(?!0)\d{3}-?\d{3}-?\d{4}"
                  errorMessage={"Invalid phone number"}
                  required={false}
                  maxLength="100"
                />
              </div>

              <div className="academic-year-container">
                Current Academic Year:
                <FormControl
                  sx={{ m: 0, minWidth: 250, maxWidth: 250 }}
                  size="small"
                >
                  <Select
                    value={selectedYear}
                    onChange={(event) => setSelectedYear(event.target.value)}
                    displayEmpty={true}
                  >
                    {availableYears.map((year, index) => {
                      return (
                        <MenuItem key={index} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
              <div className="major-container">
                CS Major or Minor: *
                <FormControl
                  sx={{ m: 0, minWidth: 250, maxWidth: 250 }}
                  size="small"
                  error={isMajorError}
                >
                  <Select
                    value={selectedMajor}
                    onChange={handleMajorChange}
                    displayEmpty
                  >
                    {availableMajorMinor.map((major, index) => {
                      return (
                        <MenuItem key={index} value={major}>
                          {major}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {isMajorError && (
                    <FormHelperText error>Missing this required field.</FormHelperText>
                  )}
                </FormControl>
              </div>
              <div className="planned-year-container">
                Planned Graduation Year: *
                <ValidateTextField
                  value={graduationYear}
                  setter={setGraduationYear}
                  pattern="20\d\d"
                  errorMessage={graduationYear ? "Invalid graduation year" : "Missing this required field"}
                  required={true}
                  maxLength="100"
                  isLoading={isLoading}
                />
              </div>
            </div>

            {/* Right-side of user profile*/}
            <div className="profile-info-right">

              <div className="jobLevel-container">
                <p className="jobLevel-title">Job Levels:</p>
                <ProfileAutocomplete
                  options={availableJobLevels}
                  selectedValue={selectedJobLevels}
                  selectedSetter={setSelectedJobLevels}
                />
              </div>

              <div className="jobType-container">
                <p className="jobType-title">Job/Research Position Types:</p>
                <ProfileAutocomplete
                  options={availableJobTypes}
                  selectedValue={selectedJobTypes}
                  selectedSetter={setSelectedJobTypes}
                />
              </div>

              <div className="jobIndustry-container">
                <p className="jobIndustry-title">Job Industry:</p>
                <ProfileAutocomplete
                  options={availableJobIndustries}
                  selectedValue={selectedJobIndustries}
                  selectedSetter={setSelectedJobIndustries}
                />
              </div>
              <div className="researchAreas-container">
                <p className="researchAreas-title">Research Areas:</p>
                <ProfileAutocomplete
                  options={availableResearchAreas}
                  selectedValue={selectedResearchAreas}
                  selectedSetter={setSelectedResearchAreas}
                />
              </div>
              <div className="researchCompensation-container">
                <p className="researchCompensation-title">Research Position Compensation:</p>
                <ProfileAutocomplete
                  options={availableCompensations}
                  selectedValue={selectedCompensations}
                  selectedSetter={setSelectedCompensations}
                />
              </div>
              <div className="jobSkills-container">
                <p className="jobSkills-title">Skills:</p>
                <ProfileAutocomplete
                  options={availableSkills}
                  selectedValue={selectedSkills}
                  selectedSetter={setSelectedSkills}
                />
              </div>
              <div className="emailOptIn-container">
                <p className="emailOptIn-title">Emails:</p>
                <FormGroup>
                  <FormControlLabel
                    control={<Switch checked={jobEmailOptIn} />}
                    label="Receive New Job Posting Emails"
                    onChange={(_event, checked) => setJobEmailOptIn(checked)}
                  />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    control={<Switch checked={resPosEmailOptIn} />}
                    label="Receive New Research Position Posting Emails"
                    onChange={(_event, checked) => setResPosEmailOptIn(checked)}
                  />
                </FormGroup>
              </div>
            </div>
            <div className="disclaimer">
              Disclaimer: Any blanks left on your profile page may affect your search results and application functionality
            </div>
          </div>
        </div>
      </div>
      <div className="profile-saveButton">
        <button className="save border" onClick={saveProfile}>
          Save
        </button>
      </div>
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarMessage("");
        }
        }
      >
        <Alert
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }
          }
          severity={snackbarMessage == "User profile updated!" ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default UserProfile;